import { PushAction } from 'redux-little-router';
import { Epic, ofType } from 'redux-observable';
import { concat, from, Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { Action } from '../../../actions';
import { updateImageEditor, UpdateImageEditorAction } from '../../../actions/banners-edit/banner-text-image';
import {
    OPEN_INTERNAL_BANNERS_EDIT,
    OpenInternalBannersEdit
} from '../../../actions/bannersEdit/internal/openInternalBannersEdit';
import {
    ShowInternalBannerEditRequestError,
    showInternalBannerEditRequestErrorAction
} from '../../../actions/bannersEdit/internal/showInternalBannerEditRequestError';
import {
    UpdateInternalBannersForEdit,
    updateInternalBannersForEditAction
} from '../../../actions/bannersEdit/internal/updateInternalBannersForEdit';
import { changeLocation } from '../../../actions/common';
import { mapInternalBannersTemplateResourcesEntries } from '../../../mappers/banners-edit/banner-edit-internal/banner-edit-internal';
import { ApiMethodExecutingError } from '../../../service/Errors';
import { getInternalBannersImageEditorsState } from '../../../service/InternalBannerHelpers/InternalBannerHelpers';
import { EpicDeps } from '../../../service/StoreFactory';
import { UrlHelpers } from '../../../service/UrlHelpers';
import { InternalBannersEditData } from '../../../typings/internalBanners';
import { State } from '../../../typings/state';

type EpicActions =
    | PushAction
    | UpdateInternalBannersForEdit
    | ShowInternalBannerEditRequestError
    | UpdateImageEditorAction;

const getInternalBannersEditData = (state: State, deps: EpicDeps): Promise<InternalBannersEditData> => {
    const {
        editInternalBanners: { campaignsIds, bannersIds, groupsIds, isNew },
        commonData: { login }
    } = state;

    if (isNew) {
        return deps.groupsDataProvider.getInternalBannersEditData(login, groupsIds, campaignsIds);
    }

    return deps.bannersDataProvider.getInternalBannersEditData(login, bannersIds, campaignsIds);
};

export const openInternalBannersEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_INTERNAL_BANNERS_EDIT),
        mergeMap(
            (action: OpenInternalBannersEdit): Observable<EpicActions> => {
                const state = store$.value;
                const {
                    editInternalBanners: { campaignsIds },
                    commonData: { login }
                } = state;

                if (campaignsIds.length === 0) {
                    const url = UrlHelpers.getBannersListUrl();

                    return of(
                        changeLocation(
                            url,
                            {
                                ulogin: login
                            },
                            false
                        )
                    );
                }

                return from(getInternalBannersEditData(state, deps)).pipe(
                    mergeMap(bannersData => {
                        return from(deps.bannersDataProvider.getInternalPlaceTemplates(bannersData.placesIds)).pipe(
                            mergeMap(placeTemplates => {
                                const entries = mapInternalBannersTemplateResourcesEntries(
                                    bannersData.banners,
                                    placeTemplates
                                );

                                return from(deps.bannersDataProvider.getInternalTemplateResources(login, entries)).pipe(
                                    mergeMap(templateResources => {
                                        return concat(
                                            of(
                                                updateImageEditor(
                                                    getInternalBannersImageEditorsState(
                                                        entries,
                                                        bannersData.images,
                                                        templateResources
                                                    )
                                                )
                                            ),
                                            of(
                                                updateInternalBannersForEditAction(
                                                    bannersData,
                                                    placeTemplates,
                                                    templateResources
                                                )
                                            )
                                        );
                                    }),
                                    catchError(
                                        (
                                            error: ApiMethodExecutingError
                                        ): Observable<ShowInternalBannerEditRequestError> => {
                                            return of(showInternalBannerEditRequestErrorAction(error));
                                        }
                                    )
                                );
                            })
                        );
                    })
                );
            }
        )
    );
};
